import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	Column,
	Grid,
	Heading,
	HeadingLevel,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { Icon } from "../../../../../Components/Icon";
import { Tile } from "../../../../../Components/Tile";
import {
	AttendanceRecordCountStatistics,
	CourseSection,
	CourseSectionNameFragment,
	useCourseAttendanceRecordCountStatisticsQuery,
	useCourseSectionAttendanceRecordsReportQuery,
} from "../../../../../Utilities";
import { CourseSectionFormat } from "../../../../Format";
import { StudentUtilities } from "../../../../Utilities";
import { AttendanceRatingBadge, CourseAttendanceRecordsTable } from "../../../_General";
import { DateFormTooltipButton, useDateFormState } from "../../../_General/Attendance/Reports/Forms";
import styles from "./styles.module.css";

export type ControlledCourseSectionReportTileProps = Omit<CourseSectionReportTileProps, "isLoading" | "statistics">;

export function ControlledCourseSectionReportTile({
	courseSection,
	date,
	...props
}: ControlledCourseSectionReportTileProps) {
	const { loading, data } = useCourseAttendanceRecordCountStatisticsQuery({
		variables: {
			date,
			studentFilters: StudentUtilities.getEnrolledInCourseSectionOnDateFilterInput(courseSection.id, date),
			courseAttendanceRecordFilters: [
				{
					courseSectionPeriod: [{ courseSectionIDEQ: courseSection.id }],
				},
			],
		},
	});

	return (
		<CourseSectionReportTile
			courseSection={courseSection}
			date={date}
			isLoading={loading}
			statistics={data?.courseAttendanceRecordCountStatistics}
			{...props}
		/>
	);
}

export type CourseSectionReportTileProps = {
	headingLevel?: HeadingLevel;
	courseSection: Pick<CourseSection, "id"> & CourseSectionNameFragment;
	date: string;
	isLoading?: boolean;
	statistics: Omit<AttendanceRecordCountStatistics, "date" | "statuses"> | null | undefined;
} & Partial<ClassNameProps & StyleProps>;

export function CourseSectionReportTile({
	headingLevel = 3,
	courseSection,
	date,
	statistics,
	className,
	style,
}: CourseSectionReportTileProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Row justify="space-between" align="center">
					<Heading level={headingLevel} noMargin>
						Attendance
					</Heading>

					<Button variant="link" size="small" onClick={openModal}>
						View
					</Button>

					<Modal.If condition={isModalOpen}>
						<CourseSectionReportModal courseSection={courseSection} initialDate={date} onClose={closeModal} />
					</Modal.If>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Grid tracks={3}>
					<Column justify="spaced-start" align="center" verticalSpacing="0.125rem">
						<span className={styles["tile__statistic__value"]}>{statistics?.absentTotal ?? 0}</span>
						<span className={styles["tile__statistic__label"]}>Total Absent</span>
					</Column>
					<Column justify="spaced-start" align="center" verticalSpacing="0.125rem">
						<span className={styles["tile__statistic__value"]}>{statistics?.lateArrivalTotal ?? 0}</span>
						<span className={styles["tile__statistic__label"]}>Total Tardy</span>
					</Column>
					<Column justify="spaced-start" align="center" verticalSpacing="0.125rem">
						<AttendanceRatingBadge rating="good" />
						<span className={styles["tile__statistic__label"]}>Attendance Rating</span>
					</Column>
				</Grid>
			</Tile.Body>
		</Tile>
	);
}

export type CourseSectionReportModalProps = {
	courseSection: Pick<CourseSection, "id"> & CourseSectionNameFragment;
	initialDate?: string;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function CourseSectionReportModal({
	courseSection,
	initialDate,
	ifRef,
	onClose,
	className,
	style,
}: CourseSectionReportModalProps) {
	const { dateFormState, date } = useDateFormState(initialDate);

	const { loading, data, error } = useCourseSectionAttendanceRecordsReportQuery({
		variables: { date, courseSectionID: courseSection.id },
	});
	const status = getListQueryStatus({ loading, data, error, resultKey: "studentConnection" });

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Attendance</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="1.5rem" style={{ height: "100%" }}>
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Row justify="spaced-start" align="center">
							<Icon.BookOpen size={24} />
							<strong>Course</strong>
							{CourseSectionFormat.name(courseSection)}
						</Row>
						<Row justify="spaced-start" align="center">
							<Icon.AddressBook size={24} />
							<strong>Attendance Rating</strong>
							<AttendanceRatingBadge rating="good" />
						</Row>
					</Column>

					<Column justify="spaced-start" verticalSpacing="1rem" className={styles["modal__report-container"]}>
						<Row justify="spaced-start" align="center">
							<Row justify="spaced-start" align="center">
								<strong>Course Attendance</strong>

								<DateFormTooltipButton formState={dateFormState} date={date} />
							</Row>
						</Row>

						<CourseAttendanceRecordsTable data={data} queryStatus={status} />
					</Column>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
